import { forwardRef } from "react";

// ── Palette (school branding + festive birthday accents) ──
const NAVY = "#123c8c";
const CREAM = "#fffdf6";
const PINK = "#e23b7a";

const DECO = "/birthday";     // same-origin transparent assets (safe for html2canvas)
const BUNTING = "/toppers/bunting.png";

// Colourful letters for the big "BIRTHDAY" wordmark (rainbow cycle).
const LETTER_COLORS = ["#2196c9", "#e23b7a", "#f4a300", "#3aa655", "#7a3ff2", "#e23b3b", "#0f9bd6"];

function initials(name) {
  const p = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || "?";
}

function RainbowWord({ text, size }) {
  return (
    <div style={{ fontFamily: "'Baloo 2', cursive", fontWeight: 800, fontSize: size, lineHeight: 1, letterSpacing: 1 }}>
      {text.split("").map((ch, i) => (
        <span key={i} style={{ color: LETTER_COLORS[i % LETTER_COLORS.length], textShadow: "0 2px 0 rgba(0,0,0,0.10)" }}>{ch}</span>
      ))}
    </div>
  );
}

const BirthdayPoster = forwardRef(function BirthdayPoster(
  { data, apiBase, photo = {} }, ref
) {
  const {
    profile = {}, student = {}, message = "", signoff = "Principal",
    signoff_name = "", birthday_date = "",
  } = data || {};

  const _cls = student.class_name != null ? String(student.class_name) : "";
  const _isPP = ["Play Group", "LKG", "UKG"].includes(_cls);
  const classLabel = _cls ? (_isPP ? _cls : `Class ${_cls}`) : "";
  const metaBits = [classLabel, student.section && `Section ${student.section}`, student.roll_number && `Roll ${student.roll_number}`].filter(Boolean);

  // Photo source: an uploaded override (data URL) wins, else the student's saved photo.
  const photoUrl = photo.override
    ? photo.override
    : (student.has_photo && student.id ? `${apiBase}/profile/photo/${student.id}` : null);

  const scale = photo.scale ?? 1;
  const px = photo.x ?? 0;
  const py = photo.y ?? 0;

  // Frame geometry — zoom/pan is done with numeric absolute positioning + an
  // object-fit:cover <img> (NO CSS transform). html2canvas rasterizes this
  // reliably, whereas a transform on an object-fit image drifts out of frame
  // on export.
  const FW = 288, FH = 336;
  const IW = FW * scale, IH = FH * scale;
  const ILEFT = (FW - IW) / 2 + px;
  const ITOP = (FH - IH) / 2 + py;

  // Sign-off resolution
  let regardsName = "Principal";
  let regardsSub = profile.name || "School";
  if (signoff === "Class Teacher") {
    regardsName = signoff_name || student.class_teacher || "Class Teacher";
    regardsSub = `Class Teacher${classLabel ? ` — ${_cls}${student.section || ""}` : ""}`;
  } else if (signoff === "School Management") {
    regardsName = "School Management";
    regardsSub = profile.name || "School";
  } else {
    regardsName = signoff_name || "Principal";
    regardsSub = profile.name || "School";
  }

  return (
    <div
      ref={ref}
      data-testid="birthday-poster"
      style={{ width: 1123, height: 794, background: CREAM, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", fontFamily: "'Montserrat', sans-serif" }}
    >
      <div style={{ position: "absolute", inset: 6, border: `2px solid ${NAVY}22`, borderRadius: 14, pointerEvents: "none", zIndex: 6 }} />

      {/* ── Header ── */}
      <div style={{ background: NAVY, padding: "12px 40px", display: "flex", alignItems: "center", gap: 20, position: "relative", zIndex: 4, flexShrink: 0 }}>
        {profile.logo ? (
          <div style={{ width: 76, height: 76, borderRadius: 10, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
            <img src={profile.logo} alt="logo" crossOrigin="anonymous" style={{ width: "94%", height: "94%", objectFit: "contain" }} />
          </div>
        ) : null}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "'Merriweather', serif", fontWeight: 900, color: "#fff", fontSize: 36, lineHeight: 1.02 }}>
            {profile.name || "School Name"}
          </div>
          {profile.address ? <div style={{ color: "#dbe6ff", fontSize: 14, marginTop: 4 }}>{profile.address}</div> : null}
          {profile.phone ? <div style={{ color: "#dbe6ff", fontSize: 14, marginTop: 2 }}>Phone: {profile.phone}</div> : null}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex" }}>
        {/* Bunting + confetti backdrop */}
        <img src={BUNTING} alt="" style={{ position: "absolute", top: 0, left: 6, width: 240, zIndex: 1 }} />
        <img src={BUNTING} alt="" style={{ position: "absolute", top: 0, right: 6, width: 300, transform: "scaleX(-1)", zIndex: 1 }} />
        <img src={`${DECO}/confetti.png`} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.38, zIndex: 0 }} />

        {/* ── Left: greeting + message + date + regards ── */}
        <div style={{ position: "relative", zIndex: 3, width: 600, padding: "44px 20px 18px 44px", display: "flex", flexDirection: "column" }}>
          <div style={{ fontFamily: "'Baloo 2', cursive", fontWeight: 800, color: "#2196c9", fontSize: 40, lineHeight: 1, letterSpacing: 1 }}>HAPPY</div>
          <div style={{ marginTop: 2 }}><RainbowWord text="BIRTHDAY" size={76} /></div>
          <div style={{ fontFamily: "'Baloo 2', cursive", fontWeight: 700, color: NAVY, fontSize: 30, letterSpacing: 4, marginTop: 2 }}>CELEBRATIONS!</div>

          {/* Student name banner */}
          <div style={{ marginTop: 16, display: "inline-block" }}>
            <div style={{ background: NAVY, color: "#fff", fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 26, letterSpacing: 1, padding: "8px 22px", borderRadius: 10, boxShadow: "0 6px 14px rgba(11,42,107,0.26)", display: "inline-block" }}>
              {student.name || "Student Name"}
            </div>
          </div>
          {metaBits.length ? (
            <div style={{ marginTop: 8, color: "#5b6a86", fontSize: 13.5, fontWeight: 600 }}>
              {metaBits.join("  ·  ")}{student.turning != null ? `  ·  Turning ${student.turning}` : ""}
            </div>
          ) : null}

          {/* Message box */}
          <div style={{ marginTop: 14, background: "#ffffff", border: `1.5px solid ${NAVY}22`, borderRadius: 14, padding: "14px 18px", boxShadow: "0 3px 10px rgba(11,42,107,0.06)", color: NAVY, fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: 16, lineHeight: 1.5 }}>
            {message || "Warm birthday wishes to our dear student! May your year be filled with happiness, good health, learning and success."}
          </div>

          <div style={{ flex: 1 }} />

          {/* Date + regards */}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginTop: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={NAVY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
              <div style={{ lineHeight: 1.25 }}>
                <div style={{ color: "#5b6a86", fontSize: 11.5, fontWeight: 600 }}>Birthday Date:</div>
                <div style={{ color: NAVY, fontSize: 17, fontWeight: 800 }}>{birthday_date || "—"}</div>
              </div>
            </div>
            <div style={{ textAlign: "left", lineHeight: 1.3 }}>
              <div style={{ color: "#5b6a86", fontSize: 12.5 }}>Regards,</div>
              <div style={{ color: NAVY, fontSize: 16, fontWeight: 800 }}>{regardsName}</div>
              <div style={{ color: "#5b6a86", fontSize: 12 }}>{regardsSub}</div>
            </div>
          </div>
        </div>

        {/* ── Right: student photo + party decorations ── */}
        <div style={{ position: "relative", zIndex: 2, flex: 1, minWidth: 0 }}>
          {/* "Have a great day" badge */}
          <div style={{ position: "absolute", top: 20, left: 6, width: 96, height: 96, borderRadius: "50%", background: "radial-gradient(circle at 35% 30%, #4db8e8, #1f8fce)", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: "#fff", fontFamily: "'Baloo 2', cursive", fontWeight: 700, fontSize: 14, lineHeight: 1.05, boxShadow: "0 6px 14px rgba(31,143,206,0.4)", border: "3px solid #fff", zIndex: 4 }}>
            Have a<br />Great<br />Day!
          </div>
          {/* Balloons */}
          <img src={`${DECO}/balloons.png`} alt="" style={{ position: "absolute", top: 6, right: 8, width: 190, zIndex: 4 }} />

          {/* Student photo frame */}
          <div style={{ position: "absolute", top: 96, left: "50%", marginLeft: -144, width: FW, height: FH, borderRadius: 18, background: NAVY, border: "6px solid #ffffff", boxShadow: "0 10px 26px rgba(11,42,107,0.30)", overflow: "hidden", zIndex: 3 }}>
            {photoUrl ? (
              <div style={{ position: "absolute", left: ILEFT, top: ITOP, width: IW, height: IH }}>
                <img
                  src={photoUrl}
                  alt={student.name}
                  crossOrigin="anonymous"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff", textAlign: "center", padding: 12 }}>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 54, opacity: 0.85 }}>{initials(student.name)}</div>
                <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600, opacity: 0.9 }}>No Student Photo Available</div>
              </div>
            )}
          </div>
          {/* Party hat sticker on the frame corner */}
          <img src={`${DECO}/party_hat.png`} alt="" style={{ position: "absolute", top: 70, left: "calc(50% - 150px)", width: 74, transform: "rotate(-18deg)", zIndex: 4, filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.2))" }} />

          {/* Cake (front, centered lower) */}
          <img src={`${DECO}/cake.png`} alt="" style={{ position: "absolute", bottom: -6, left: "50%", transform: "translateX(-58%)", width: 220, zIndex: 5, filter: "drop-shadow(0 6px 8px rgba(0,0,0,0.18))" }} />
          {/* Gift box (bottom-right) */}
          <img src={`${DECO}/gift.png`} alt="" style={{ position: "absolute", bottom: 4, right: 14, width: 132, zIndex: 5, filter: "drop-shadow(0 6px 8px rgba(0,0,0,0.18))" }} />
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ background: NAVY, color: "#cdddff", textAlign: "center", padding: "7px 0", fontSize: 11.5, fontWeight: 600, letterSpacing: 0.3, position: "relative", zIndex: 4, flexShrink: 0 }}>
        © {profile.name || "School"} &nbsp;|&nbsp; Built by ZeroLabs Digitals
      </div>
    </div>
  );
});

export default BirthdayPoster;
